import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { IGeofence } from "@interfaces/IGeofences";
import { IJourney } from "@interfaces/IJourney";
import { IVehicle } from "@interfaces/IVehicle";
import { GeofenceService, IGeofenceVisit } from "@services/geofence.service";
import { GroupService } from "@services/group.service.ajs";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import * as moment from "moment";

export interface IVehicleUtilisationRecord {
    vehicle?: IVehicle;

    homeGeofence?: IGeofence;
    geofenceVisits?: IGeofenceVisit[];

    journeys?: IJourney[];

    utilisationRecords: IVehicleUtilisationPeriod[];
    utilisationPercentage: number;
}

interface IDatedPeriod {
    Id: number;
    StartUtc: string;
    EndUtc: string;
}

export interface IVehicleUtilisationPeriod {
    id: number;
    left: number;
    width: number;
    color: string;
    tooltip: string;
    startMoment: moment.Moment;
    endMoment: moment.Moment;
}

export class UtilisationService {
    static $inject = ["geofenceService", "groupService", "veriDate", "translationService"];

    constructor(
        private readonly geofenceService: GeofenceService,
        private readonly groupService: GroupService,
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService
    ) {
    }

    createUtilisationRecordsForJourneys(startDate: Date, endDate: Date, vehicles: IVehicle[], journeys: IJourney[]): IVehicleUtilisationRecord[] {
        var vehicleUtilisationRecords: IVehicleUtilisationRecord[] = [];

        vehicles.forEach(v => {
            const utilisationRecord = this.createJourneyUtilisationRecordForVehicle(startDate, endDate, v, journeys);
            vehicleUtilisationRecords.push(utilisationRecord);
        });

        this.orderUtilisationRecords(vehicleUtilisationRecords);

        return vehicleUtilisationRecords;
    }

    createUtilisationRecordsForDepots(startDate: Date, endDate: Date, groups: IGroupWithIds[], vehicles: IVehicle[], geofenceVisits: IGeofenceVisit[], geofences: IGeofence[]): IVehicleUtilisationRecord[] {
        var vehicleUtilisationRecords: IVehicleUtilisationRecord[] = [];

        const depotsWithHomeLocations = this.groupService.findDepotsWithHomeLocations(groups);
        const visitsToDepotsOrHomeLocations = this.geofenceService.findGeofenceVisitsToDepotsOrHomeLocations(geofenceVisits, depotsWithHomeLocations);

        vehicles.forEach(v => {

            const utilisationRecord = this.createDepotUtilisationRecordForVehicle(startDate, endDate, v, visitsToDepotsOrHomeLocations);
            const vehicleDepots = this.groupService.findGroupIdsAssetBelongsTo(depotsWithHomeLocations, v.Id);

            // Specify geofence
            if (utilisationRecord.vehicle.GeofenceId) {
                utilisationRecord.homeGeofence = this.geofenceService.findGeofence(geofences, utilisationRecord.vehicle.GeofenceId);
            }
            else if (vehicleDepots.length > 0) {
                const vehicleDepot = this.groupService.findGroup(depotsWithHomeLocations, vehicleDepots[0]);
                utilisationRecord.homeGeofence = this.geofenceService.findGeofence(geofences, vehicleDepot.GeofenceId);
            }
            else {
                // If we have no depot or home location set, clear the list
                utilisationRecord.utilisationRecords = [];
                utilisationRecord.utilisationPercentage = 0;
            }

            vehicleUtilisationRecords.push(utilisationRecord);
        });

        this.orderUtilisationRecords(vehicleUtilisationRecords);

        return vehicleUtilisationRecords;
    }

    orderUtilisationRecords(vehicleUtilisationRecords: IVehicleUtilisationRecord[]) {

        // Show most utilised first
        vehicleUtilisationRecords.sort((x, y) => {
            var totalWidth1 = x.utilisationRecords.reduce((prev, cur) => prev + cur.width, 0);
            var totalWidth2 = y.utilisationRecords.reduce((prev, cur) => prev + cur.width, 0);

            return totalWidth2 - totalWidth1;
        });
    }

    // ---

    private createJourneyUtilisationRecordForVehicle(startDate: Date, endDate: Date, vehicle: IVehicle, journeys: IJourney[]): IVehicleUtilisationRecord {
        const vehicleJourneys = journeys.filter(journey => journey.VehicleId == vehicle.Id && this.validateJourney(journey));

        const vu: IVehicleUtilisationRecord = {
            vehicle: vehicle,
            journeys: vehicleJourneys,
            utilisationRecords: [],
            utilisationPercentage: 0
        };

        var startOfRange = this.veriDate.createMoment(startDate);
        var endOfRange = this.veriDate.createMoment(endDate);
        var minutesInRange = endOfRange.diff(startOfRange, "minutes");


        var previousJourney: IJourney = null;
        vehicleJourneys.forEach(journey => {

            this.createAndUpdateUtilisationRecord(vu, previousJourney, journey, startOfRange, endOfRange, minutesInRange);

            previousJourney = journey;
        });

        return vu;
    }

    private createDepotUtilisationRecordForVehicle(startDate: Date, endDate: Date, vehicle: IVehicle, geofenceVisits: IGeofenceVisit[]): IVehicleUtilisationRecord {
        const vehicleGeofenceVisits = geofenceVisits.filter(geofenceVisit => geofenceVisit.VehicleId == vehicle.Id && this.validateGeofenceVisit(geofenceVisit));

        var vu: IVehicleUtilisationRecord = {
            vehicle: vehicle,
            geofenceVisits: vehicleGeofenceVisits,
            utilisationRecords: [],
            utilisationPercentage: 0
        };

        const startOfRange = this.veriDate.createMoment(startDate);
        const endOfRange = this.veriDate.createMoment(endDate);
        const minutesInRange = endOfRange.diff(startOfRange, "minutes");

        var previousGeofenceVisit: IGeofenceVisit = null;
        vehicleGeofenceVisits.forEach(geofenceVisit => {

            this.createAndUpdateUtilisationRecord(vu, previousGeofenceVisit, geofenceVisit, startOfRange, endOfRange, minutesInRange, true);

            previousGeofenceVisit = geofenceVisit;
        });

        if (!previousGeofenceVisit || endOfRange.isAfter(previousGeofenceVisit.EndUtc)) {

            this.createAndUpdateUtilisationRecord(vu, previousGeofenceVisit, null, startOfRange, endOfRange, minutesInRange, true);
        }

        return vu;
    }

    private createAndUpdateUtilisationRecord(vu: IVehicleUtilisationRecord, previousPeriod: IDatedPeriod, datedPeriod: IDatedPeriod, startOfRange: moment.Moment, endOfRange: moment.Moment, minutesInRange: number, reverse = false) {

        if (!previousPeriod && !datedPeriod) {
            return;
        }

        let startMoment: moment.Moment;
        let endMoment: moment.Moment;

        if (reverse) {
            if (previousPeriod && previousPeriod.EndUtc) {
                startMoment = this.veriDate.createMoment(previousPeriod.EndUtc);
            }
            else {
                startMoment = previousPeriod ? endOfRange : startOfRange;
            }

            if (datedPeriod && this.veriDate.createMoment(datedPeriod.StartUtc).isBefore(startOfRange)) {
                endMoment = startOfRange;
            }
            else {
                endMoment = datedPeriod ? this.veriDate.createMoment(datedPeriod.StartUtc) : endOfRange;
            }
        }
        else {
            startMoment = this.veriDate.createMoment(datedPeriod.StartUtc);
            endMoment = this.veriDate.createMoment(datedPeriod.EndUtc);
        }

        // Clamp to end of range
        if (endMoment.isAfter(endOfRange)) {
            endMoment = endOfRange.clone();
        }

        const jobToleranceWidth = 0.05;
        const period = this.createUtilisationRecord(startMoment, endMoment, startOfRange, minutesInRange, datedPeriod ? datedPeriod.Id : -1);
        if (period.width >= jobToleranceWidth) {

            vu.utilisationRecords.push(period);
        }

        vu.utilisationPercentage += period.width;
    }

    private createUtilisationRecord(startMoment: moment.Moment, endMoment: moment.Moment, startOfRange: moment.Moment, minutesInRange: number, id?: number): IVehicleUtilisationPeriod {
        const diffMinutesStart = startMoment.diff(startOfRange, 'minutes');
        const diffMinutesEnd = endMoment.diff(startOfRange, 'minutes');
        const diffMinutesWidth = diffMinutesEnd - diffMinutesStart;

        const left = diffMinutesStart / minutesInRange * 100;
        let width = diffMinutesWidth / minutesInRange * 100;

        if (left + width > 100) {
            width -= left + width - 100;
        }

        if (width < 0) {
            width = 0;
        }

        const period: IVehicleUtilisationPeriod = {
            id: id,
            left: left,
            width: width,
            color: "blue",
            tooltip: this.translationService.t('utilisationService.utilised', [this.veriDate.formatDate(startMoment), this.veriDate.formatDate(endMoment)]),
            startMoment: startMoment,
            endMoment: endMoment
        };

        return period;
    }

    private validateGeofenceVisit(geofenceVisit: IGeofenceVisit): boolean {

        const startTime = new Date(geofenceVisit.StartUtc);
        const endTime = geofenceVisit.EndUtc ? new Date(geofenceVisit.EndUtc) : new Date();
        return endTime.getTime() - startTime.getTime() >= 0;
    }

    private validateJourney(journey: IJourney): boolean {

        const startTime = new Date(journey.StartUtc);
        const endTime = journey.EndUtc ? new Date(journey.EndUtc) : new Date();
        return endTime.getTime() - startTime.getTime() >= 0;
    }
}
